import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy, ViewChild, effect, inject, untracked } from '@angular/core';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { deviceTableRow } from 'src/app/models/transforms';
import { SolutionSpace } from 'src/app/models/solution';
import { DevicesService } from 'src/app/services/api/devices.service';
import { UserService } from 'src/app/services/api/user.service';
import { ResponseHandlerService } from 'src/app/services/responseHandler.service';
import { ResponsiveService } from 'src/app/services/responsive.service';
import { TableColumn, TableRow } from 'src/app/shared/table/table.component';
import { DevicesSortOrder, SortDirection } from 'src/app/models/page-request';
import { TranslatePipe } from '@ngx-translate/core';
import { TableComponent } from '../../shared/table/table.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { SearchFieldComponent } from '../../shared/search-field/search-field.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfigDialogComponent } from 'src/app/zx-devices/zx-device-form/config-dialog/config-dialog.component';
import { InfoPageComponent } from 'src/app/shared/info-page/info-page.component';
import { SnackbarService } from 'src/app/services/snackbar.service';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    MatButtonModule,
    MatMenuModule,
    AngularSvgIconModule,
    SearchFieldComponent,
    MatCardModule,
    MatProgressSpinnerModule,
    TableComponent,
    TranslatePipe,
    InfoPageComponent,
  ],
})
export class DevicesComponent implements OnInit {
  privateparamSubscription: Subscription;
  solutionSpace: SolutionSpace;
  privaterouteSub: Subscription | undefined;
  devices: TableRow[];
  @ViewChild(TableComponent) private table?: TableComponent;
  private readonly bp = inject(BreakpointService);
  private loadRequestId = 0;
  private wasPhone = this.bp.isPhone();
  groups: Array<string> = [];
  types: string[];
  isLoaded = false;
  pageSize = 50;
  totalItemCount = 0;
  resultCount = 0;
  pageNumber = 1;

  // search
  searchText: string | undefined;
  // type
  deviceTypes: { value: string; label: string; icon: string }[] = [
    { value: '_all_', label: 'All', icon: '/assets/icons/filter.svg' },
    { value: '0', label: 'Logistics Hub Utilization', icon: '/assets/icons/utilization-device.svg' },
    { value: '1', label: 'Logistics Hub Humidity', icon: '/assets/icons/icon-16-humidity.svg' },
  ];
  deviceType: string | undefined;
  displayedFilter = { label: '', icon: '' };
  sortBy = DevicesSortOrder.LAST_COMMUNICATED;
  displayedColumns: TableColumn[] = [
    { key: 'name', name: 'Device ID', isSortable: true },
    { key: 'componentName', name: 'Component name' },
    { key: 'type', name: 'Device type' },
    { key: 'firmware', name: 'FW Version', isSortable: true },
    {
      key: 'lastCommunicationTime',
      name: 'Last communicated',
      isSortable: true,
    },
    { key: 'batteryLevel', name: 'Battery Level', isSortable: true },
  ];
  sortDirection: SortDirection = SortDirection.DESC;

  constructor(
    private devicesService: DevicesService,
    private userService: UserService,
    private responseHandlerService: ResponseHandlerService,
    private titleService: Title,
    private responsiveService: ResponsiveService,
    private dialog: MatDialog,
    private snackBar: SnackbarService
  ) {
    this.displayedFilter = { label: this.deviceTypes[0].label, icon: '/assets/icons/filter.svg' };
    // Phones grow one list while wider screens page through it: switching between the two starts again at page 1.
    effect(() => {
      const phone = this.bp.isPhone();
      untracked(() => {
        if (phone === this.wasPhone) return;
        this.wasPhone = phone;
        if (!this.isLoaded) return;
        this.pageNumber = 1;
        this.loadDevices();
      });
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Device Pool - Admin');
    this.userService.getGroupsForComponentCreation().subscribe({
      next: (result) => {
        this.groups = result.map((group) => group.id);
        this.devicesService
          .getAllDevices(
            {
              groupIds: this.groups,
            },
            {
              pageNumber: this.pageNumber,
              pageSize: this.pageSize,
              sortOrder: DevicesSortOrder.LAST_COMMUNICATED,
              sortDirection: SortDirection.DESC,
            }
          )

          .subscribe((devicesPageable) => {
            this.totalItemCount = devicesPageable.totalItemCount;
            this.resultCount = devicesPageable.totalItemCount;
            if (devicesPageable.items && devicesPageable.items.length > 0) {
              this.devices = devicesPageable.items.map((d) => deviceTableRow(d));
            }
            this.isLoaded = true;
          });
      },
      error: (error: HttpErrorResponse) => {
        this.responseHandlerService.error(error);
      },
    });
  }

  setSearchControl(search: string): void {
    this.searchText = search.replace(/:/g, '-');
    this.loadDevices();
  }
  setDeviceTypeControl(deviceTypeControl: { label: string; value: string; icon: string }): void {
    this.deviceType = deviceTypeControl.value;
    this.displayedFilter.label = deviceTypeControl.label;
    this.displayedFilter.icon = deviceTypeControl.icon;
    this.loadDevices();
  }

  isMobile(): boolean {
    return this.responsiveService.isMobile();
  }

  setSorting(event: Sort): void {
    this.sortDirection = event.direction == 'asc' ? SortDirection.ASC : SortDirection.DESC;
    switch (event.active) {
      case 'name': {
        this.sortBy = DevicesSortOrder.IDENTIFIER;
        break;
      }
      case 'firmware': {
        this.sortBy = DevicesSortOrder.FIRMWARE;
        break;
      }
      case 'lastCommunicationTime': {
        this.sortBy = DevicesSortOrder.LAST_COMMUNICATED;
        break;
      }
      case 'batteryLevel': {
        this.sortBy = DevicesSortOrder.BATTERY_LEVEL;
        break;
      }
    }
    this.loadDevices();
  }

  /**
   * Loads `pageNumber`. On phones the list grows: `append` adds the page to the rows already shown, any other load
   * (search, filter, sort) starts again at page 1.
   */
  loadDevices(append = false): void {
    const solution: SolutionSpace | undefined =
      this.deviceType === '0'
        ? SolutionSpace.Utilization
        : this.deviceType === '1'
        ? SolutionSpace.Humidity
        : undefined;
    // A newer load (another search, filter, sort or page) makes this answer stale.
    const requestId = ++this.loadRequestId;
    if (!append && this.bp.isPhone()) this.pageNumber = 1;
    // Appending keeps the list on screen, so the scroll position holds.
    this.isLoaded = append;

    this.devicesService
      .getAllDevices(
        {
          groupIds: this.groups,
          solutionSpace: solution,
          search: this.searchText,
        },
        {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          sortOrder: this.sortBy,
          sortDirection: this.sortDirection,
        }
      )
      .subscribe({
        next: (devicesPageable) => {
          if (requestId !== this.loadRequestId) return;
          this.totalItemCount = devicesPageable.totalItemCount;
          this.resultCount = devicesPageable.totalItemCount;
          const rows = devicesPageable.items.map((d) => deviceTableRow(d));
          this.devices = append ? [...this.devices, ...rows] : rows;

          this.isLoaded = true;
        },
        error: (error: HttpErrorResponse) => {
          if (requestId !== this.loadRequestId) return;
          this.responseHandlerService.error(error);
          this.isLoaded = true;
          if (append) {
            // Back to the last page that did load; the table offers a retry for the next one.
            this.pageNumber--;
            this.table?.loadMoreFailed();
          }
        },
      });
  }

  handlePaginationEvent(event: PageEvent): void {
    const { pageIndex, pageSize } = event;
    if (this.pageNumber === pageIndex && this.pageSize === pageSize) return;

    if (this.pageNumber !== pageIndex) {
      this.pageNumber = pageIndex;
    }
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
    }
    // On phones a higher page index means "append the next page".
    this.loadDevices(this.bp.isPhone() && pageIndex > 1);
  }

  openDeviceConfigurationDialog(selectedRow: TableRow): void {
    this.devicesService.getDeviceConfiguration(selectedRow.name.value as string).subscribe({
      next: (config) => {
        var dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.minHeight = '20vh';
        dialogConfig.minWidth = '40vw';
        dialogConfig.data = { identifier: selectedRow.name.value, configuration: config };
        const dialogRef = this.dialog.open(ConfigDialogComponent, dialogConfig);
      },
      error: () => {
        var responseObj = {
          title: 'Error',
          message: 'Error while fetching device configuration',
        };
        this.snackBar.openSnackBar(responseObj, 'ERROR');
      },
    });
  }
}
